import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, Building, ArrowRight, ArrowLeft } from 'lucide-react';
import { UserHeader } from '../components/UserHeader';
import { goBack } from '../utils/navigation';

export default function RecurseroLandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  // Verificación adicional de autenticación
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Guardar redirect para volver al recursero después del login
      sessionStorage.setItem('redirectAfterLogin', '/recursero');
      navigate('/login', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  // Mostrar loading mientras verifica auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Si no está autenticado, no renderizar nada (el useEffect redirigirá)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header unificado */}
      <UserHeader />
      
      {/* Subtítulo de sección */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Recursero</h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Guía de Recursos del Colegio Profesional de Trabajadorxs Sociales de Lomas de Zamora
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            ¿Qué recursero querés consultar?
          </h2>
          <p className="text-gray-500">
            Elegí el tipo de búsqueda que necesitás realizar
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Recursero Institucional */}
          <Card 
            className="hover:shadow-xl transition-all cursor-pointer border-2 hover:border-primary group"
            onClick={() => navigate('/recursero/institucional')}
          >
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <Building className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Recursero Institucional
              </h3>
              <p className="text-gray-500 mb-6 text-sm">
                Buscá instituciones por territorio, área de intervención o tipo. 
                Encontrá datos de contacto, horarios y notas de colegas.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                <Badge variant="secondary" className="bg-blue-50 text-blue-700">Hospitales</Badge>
                <Badge variant="secondary" className="bg-green-50 text-green-700">Servicios Locales</Badge>
                <Badge variant="secondary" className="bg-purple-50 text-purple-700">ONGs</Badge>
                <Badge variant="secondary" className="bg-orange-50 text-orange-700">Centros de Referencia</Badge>
              </div>
              <Button className="w-full group-hover:bg-primary">
                Buscar instituciones
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Recursero de Colegas */}
          <Card 
            className="hover:shadow-xl transition-all cursor-pointer border-2 hover:border-emerald-500 group"
            onClick={() => navigate('/recursero/colegas')}
          >
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                <MapPin className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Recursero de Colegas
              </h3>
              <p className="text-gray-500 mb-6 text-sm">
                Encontrá trabajadorxs sociales por territorio y área de trabajo. 
                Contactá colegas para articular y derivar.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">Por municipio</Badge>
                <Badge variant="secondary" className="bg-teal-50 text-teal-700">Por área</Badge>
                <Badge variant="secondary" className="bg-cyan-50 text-cyan-700">Contacto directo</Badge>
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                Buscar colegas
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Botón volver */}
        <div className="text-center mt-8">
          <Button 
            variant="outline" 
            onClick={() => goBack()}
            data-testid="back-to-recursero-menu-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
      </main>
    </div>
  );
}
